import { ChevronDownIcon, ChevronRightIcon } from 'lucide-react'
import type { KeyboardEvent } from 'react'

import { numberToCurrency } from '@/utils'
import { countOf, monthSpanText } from '../portfolioText'
import { VerdictBadge } from '../VerdictBadge'
import type { InstitutionGroup } from './accountLines'
import { CELL_CLASS, FigureCell, TwoLineCell } from './cells'

interface GroupRowProps {
  group: InstitutionGroup
  isOpen: boolean
  showChange: boolean
  onToggle: (institution: string) => void
}

const accountsLine = (group: InstitutionGroup) => {
  const accounts = countOf(group.accounts.length, 'account')
  return group.endedCount > 0
    ? `${accounts}, ${group.endedCount} ended`
    : accounts
}

// One institution's accounts folded into a line, with their totals and one verdict.
export const GroupRow = ({
  group,
  isOpen,
  showChange,
  onToggle,
}: GroupRowProps) => {
  const toggle = () => onToggle(group.institution)
  const toggleOnEnter = (event: KeyboardEvent) => {
    if (event.key === 'Enter') toggle()
  }
  const Chevron = isOpen ? ChevronDownIcon : ChevronRightIcon

  return (
    <tr
      tabIndex={0}
      aria-expanded={isOpen}
      onClick={toggle}
      onKeyDown={toggleOnEnter}
      className="cursor-pointer hover:[&>td]:bg-slate-50"
    >
      <TwoLineCell
        main={
          <span className="font-semibold">
            <Chevron className="mr-1.5 inline size-3.5 align-[-2px] text-blue-5" />
            {group.institution}
          </span>
        }
        sub={<span className="pl-5">{accountsLine(group)}</span>}
      />
      <TwoLineCell
        main={countOf(group.statementCount, 'statement')}
        sub={
          group.firstStatementMonth && group.lastStatementMonth
            ? monthSpanText(group.firstStatementMonth, group.lastStatementMonth)
            : undefined
        }
      />
      <FigureCell quiet={group.value == null}>
        {group.value == null ? 'No total' : numberToCurrency(group.value)}
      </FigureCell>
      {showChange && (
        <FigureCell quiet={group.change == null}>
          {group.change == null ? 'No total' : numberToCurrency(group.change)}
        </FigureCell>
      )}
      <FigureCell>{numberToCurrency(group.netMoney)}</FigureCell>
      <td className={CELL_CLASS}>
        <VerdictBadge
          status={group.badge.status}
          sentence={group.badge.sentence}
        />
      </td>
    </tr>
  )
}
